import * as THREE from 'three';
import {
	GLTFLoader
} from 'three/examples/jsm/loaders/GLTFLoader';
import {
	AssetManager
} from './AssetManager.js';
import {
	MeshBVH,
	MeshBVHUniformStruct,
	shaderStructs,
	shaderIntersectFunction,
	SAH
} from 'three-mesh-bvh';
import {
	OrbitControls
} from 'three/examples/jsm/controls/OrbitControls';
import {
	LineSegments2
} from 'three/examples/jsm/lines/LineSegments2.js';
import {
	LineSegmentsGeometry
} from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import {
	LineMaterial
} from 'three/examples/jsm/lines/LineMaterial.js';
import {
	ThickGridHelper
} from './thickGridHelper.js';
import {
	CSS2DRenderer,
	CSS2DObject
} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import diamondVertexShader from './shaders/diamond.vert?raw';
import diamondFragmentShader from './shaders/diamond.frag?raw';

async function main() {
	const mainid = document.getElementById('main');
	const {
		width: clientWidth,
		height: clientHeight
	} = mainid.getBoundingClientRect();

	const scene = new THREE.Scene();
	const camera = new THREE.PerspectiveCamera(75, clientWidth / clientHeight, 0.1, 500);
	let isInitialAnimationComplete = false;
	let renderer;
	let initializationAttempts = 0;
	let contextLossCount = 0;
	const maxAttempts = 3;
	let mainContextLost = false;
	let helpersShutdown = false;

	function showWebGLError(message = null) {
		const errorDiv = document.createElement('div');
		errorDiv.id = 'webgl-error';
		errorDiv.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0,0,0,0.8);
            color: white;
            padding: 20px;
            border-radius: 10px;
            text-align: center;
            z-index: 1000;
        `;

		const defaultMessage = `
            <h3 style="color: white;">WebGL Issue Detected</h3>
            <p>Your browser or device is having trouble with WebGL graphics.</p>
            <p><strong>Please try:</strong></p>
            <ul style="text-align: left;">
                    <li>Switching browser or device</li>
                    <li>Restart your browser</li>
                    <li>Enabling hardware acceleration in browser settings</li>
                </ul>
                <p>Only the landing page and the about page uses WebGL graphics. If switching browser isn't an option please proceed to the Portfolio by pressing the button below. The Experience page and contact page are also both safe. Sorry for any inconvenience.</p>
            <button href="./portfolio.html" class="btn-outline-current-color rounded-3 mb-3">Portfolio Page</button>
        `;

		errorDiv.innerHTML = message || defaultMessage;
		document.body.appendChild(errorDiv);
	}

	function handleContextLoss(contextType = 'main') {
		contextLossCount++;
		console.warn(`WebGL context lost (${contextType}) - attempt ${contextLossCount}/${maxAttempts}`);

		if (contextType === 'main') {
			mainContextLost = true;
			shouldAnimate = false;

			// Only shutdown helpers if any were actually initialized
			if (gimbalhelperloaded || ringhelperloaded) {
				shutdownHelpers();
			}

			if (contextLossCount >= maxAttempts) {
				console.error('Maximum context loss attempts reached');
				showWebGLError();
			} else {
				showTemporaryWarning(`Graphics context lost - attempting to recover... (${contextLossCount}/${maxAttempts})`);
			}
		} else {
			// Helper context lost - but only handle if the helper was actually loaded
			const helperWasLoaded = (contextType === 'gimbal' && gimbalhelperloaded) ||
				(contextType === 'ring' && ringhelperloaded);

			if (!helperWasLoaded) {
				console.log(`${contextType} context lost but helper wasn't loaded yet - ignoring`);
				return;
			}

			if (mainContextLost) {
				console.log(`${contextType} context lost but main already failed - shutting down`);
				shutdownHelpers();
			} else {
				console.log(`${contextType} helper context lost - attempting recovery`);
				// Try to recover just this helper after a delay
				setTimeout(() => {
					if (contextType === 'gimbal' && !helpersShutdown) {
						reinitializeGimbal();
					} else if (contextType === 'ring' && !helpersShutdown) {
						reinitializeRingHelper();
					}
				}, 1000);
			}
		}
	}

	function showTemporaryWarning(message) {
		// Remove existing warning if present
		const existingWarning = document.getElementById('webgl-warning');
		if (existingWarning) {
			existingWarning.remove();
		}

		const warningDiv = document.createElement('div');
		warningDiv.id = 'webgl-warning';
		warningDiv.style.cssText = `
        position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0,0,0,0.8);
            color: white;
            padding: 20px;
            border-radius: 10px;
            text-align: center;
            z-index: 1000;
    `;
		warningDiv.textContent = message;
		document.body.appendChild(warningDiv);

		// Auto-remove after 5 seconds
		setTimeout(() => {
			if (warningDiv && warningDiv.parentNode) {
				warningDiv.remove();
			}
		}, 5000);
	}

	function createRenderer() {
		try {
			renderer = new THREE.WebGLRenderer({
				alpha: true,
				antialias: false,
				powerPreference: "default",
				failIfMajorPerformanceCaveat: false // Don't fail on performance issues
			});
			// Test if context is immediately available
			const gl = renderer.getContext();
			if (!gl || gl.isContextLost()) {
				throw new Error('WebGL context not available');
			}

			renderer.setPixelRatio(window.devicePixelRatio);
			renderer.setSize(clientWidth, clientHeight);
			renderer.toneMapping = THREE.ACESFilmicToneMapping;
			renderer.toneMappingExposure = 1.042;
			return true;

		} catch (error) {
			console.error('Failed to create WebGL renderer:', error);

			if (renderer) {
				renderer.dispose();
				renderer = null;
			}

			initializationAttempts++;

			if (initializationAttempts < maxAttempts) {
				console.log(`Retrying renderer creation (attempt ${initializationAttempts + 1}/${maxAttempts})...`);
				setTimeout(() => {
					if (createRenderer()) {
						initializeScene();
					}
				}, 1000);
				return false;
			} else {
				showWebGLError();
				return false;
			}
		}
	}

	function handleContextRestored(contextType = 'main') {
		console.log(`WebGL context restored (${contextType})`);

		if (contextType === 'main') {
			mainContextLost = false;
			contextLossCount = Math.max(0, contextLossCount - 1);
			const existingWarning = document.getElementById('webgl-warning');
			if (existingWarning) {
				existingWarning.remove();
			}

			try {
				reinitializeWebGL();
				if (helpersShutdown) {
					helpersShutdown = false;
					setTimeout(() => {
						if (gimbalhelperloaded) reinitializeGimbal();
						if (ringhelperloaded) reinitializeRingHelper();
					}, 500);
				}

				showTemporaryWarning('Graphics restored successfully!');

				if (!shouldAnimate) {
					shouldAnimate = true;
					animate();
					fadein([renderer.domElement]);
				}
			} catch (error) {
				console.error('Failed to restore WebGL context:', error);
				handleContextLoss(contextType);
			}
		}
	}
	async function initializeScene() {
		if (!renderer) {
			console.error('Cannot initialize scene - no renderer available');
			return;
		}
		// CONTEXT LOSS HANDLING:
		const canvas = renderer.domElement;
		canvas.addEventListener('webglcontextlost', function(event) {
			event.preventDefault();
			handleContextLoss('main');
			fadeout();
		});
		canvas.addEventListener('webglcontextrestored', function() {
			handleContextRestored('main');
		});
		document.addEventListener('visibilitychange', function() {
			if (document.hidden) {
				shouldAnimate = false;
			} else {
				shouldAnimate = true;
				animate();
			}
		});
		document.querySelector('#bg').appendChild(renderer.domElement);
		camera.position.setX(0);
		camera.position.setY(6);
		camera.position.setZ(6);
		const controls = new OrbitControls(camera, renderer.domElement);
		controls.enableDamping = true;
		controls.zoomSpeed = 0.5;
		controls.maxDistance = 50;
		controls.minDistance = 0.5;
		controls.target.set(0, 4, 0);
		controls.enabled = false;

		// Lights
		const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 5);
		hemiLight.color.setHSL(0.6, 1, 0.6);
		hemiLight.groundColor.setHSL(0.095, 1, 0.75);
		hemiLight.position.set(0, 50, 0);
		scene.add(hemiLight);
		const dirLight = new THREE.DirectionalLight(0xffffff, 4);
		dirLight.color.setHSL(0.1, 1, 0.95);
		dirLight.position.set(0.9, 0.5, 0.75);
		dirLight.position.multiplyScalar(30);
		scene.add(dirLight);
		const gltfLoader = new GLTFLoader();
		const cubeTextureLoader = new THREE.CubeTextureLoader();

		const diamondPromise = AssetManager.loadGLTFAsync("models/diamond.glb");
		const platPromise = gltfLoader.loadAsync('models/PlatinumQ2.glb');
		const goldPromise = gltfLoader.loadAsync('models/GoldQ2.glb');
		const envPromise = cubeTextureLoader.loadAsync([
			"img/skybox/Box_Right.png",
			"img/skybox/Box_Left.png",
			"img/skybox/Box_Top.png",
			"img/skybox/Box_Bottom.png",
			"img/skybox/Box_Front.png",
			"img/skybox/Box_Back.png"
		]);
		const env2Promise = cubeTextureLoader.loadAsync([
			"img/skybox2/Box_Right.png",
			"img/skybox2/Box_Left.png",
			"img/skybox2/Box_Top.png",
			"img/skybox2/Box_Bottom.png",
			"img/skybox2/Box_Front.png",
			"img/skybox2/Box_Back.png"
		]);
		let diamondGLTF, platGLTF, goldGLTF, environment, environment2;
		try {
			[diamondGLTF, platGLTF, goldGLTF, environment, environment2] = await Promise.all([
				diamondPromise,
				platPromise,
				goldPromise,
				envPromise,
				env2Promise
			]);
		} catch (error) {
			showWebGLError(`
				<h3>Asset Loading Error</h3>
				<p>One or more assets failed to load. Please check your internet connection or try refreshing the page.</p>
				<pre>${error.message}</pre>
			`);
			console.error('Asset loading failed:', error);
			return; // Stop further initialization
		}
		environment.encoding = THREE.sRGBEncoding;
		environment2.encoding = THREE.sRGBEncoding;

		// Objects
		const diamondGeo = diamondGLTF.scene.children[0].children[0].children[0].children[0].children[0].geometry;

		const makeDiamond = (geo, {
			color = new THREE.Color(1, 1, 1),
			ior = 2.418,
			isStatic = false
		} = {}) => {
			if (!makeDiamond.sharedGeometry) {
				const mergedGeometry = geo.clone();
				mergedGeometry.boundsTree = new MeshBVH(mergedGeometry.toNonIndexed(), {
					lazyGeneration: false,
					strategy: SAH,
					maxLeafTris: 15
				});
				makeDiamond.sharedGeometry = mergedGeometry;
				makeDiamond.sharedCollider = new THREE.Mesh(mergedGeometry);
				makeDiamond.sharedCollider.material.wireframe = true;
				makeDiamond.sharedCollider.material.opacity = 0.5;
				makeDiamond.sharedCollider.material.transparent = true;
				makeDiamond.sharedCollider.visible = false;
				makeDiamond.sharedCollider.boundsTree = mergedGeometry.boundsTree;
				makeDiamond.sharedBVH = new MeshBVHUniformStruct();
				makeDiamond.sharedBVH.updateFrom(makeDiamond.sharedCollider.boundsTree);
				makeDiamond.wireframeMaterial = new THREE.MeshStandardMaterial({
					wireframe: true,
					color: 0x4E77C7
				});
			}
			const material = new THREE.ShaderMaterial({
				uniforms: {
					envMap: {
						value: environment
					},
					bvh: {
						value: makeDiamond.sharedBVH
					},
					bounces: {
						value: isStatic ? 2 : 3
					}, // Reduce bounces for static diamonds first value
					color: {
						value: color
					},
					ior: {
						value: ior
					},
					correctMips: {
						value: true
					},
					projectionMatrixInv: {
						value: camera.projectionMatrixInverse
					},
					viewMatrixInv: {
						value: camera.matrixWorld
					},
					chromaticAberration: {
						value: !isStatic
					}, // Disable for static diamonds with !isStatic
					aberrationStrength: {
						value: isStatic ? 0.0 : 0.01
					}, // Reduce static diamonds first value
					resolution: {
						value: new THREE.Vector2(clientWidth, clientHeight)
					}
				},
				vertexShader: diamondVertexShader,
				fragmentShader: `
            ${ shaderStructs }
            ${ shaderIntersectFunction }
            ${diamondFragmentShader}
        `
			});
			// Safari optimization: Share materials when possible
			if (isStatic && makeDiamond.staticMaterial) {
				return new THREE.Mesh(makeDiamond.sharedGeometry, makeDiamond.staticMaterial);
			} else if (isStatic) {
				makeDiamond.staticMaterial = material;
			}

			const diamond = new THREE.Mesh(makeDiamond.sharedGeometry, material);
			return diamond;
		};
		const QParent = new THREE.Object3D();
		const Pivot = new THREE.Object3D();
		scene.add(Pivot);
		Pivot.position.set(0, 2.8, 0);
		QParent.position.set(0, -2.8, 0);
		const DiamondQ3 = makeDiamond(diamondGeo, {
			isStatic: false
		});
		DiamondQ3.scale.set(1, 1, 1);
		DiamondQ3.position.set(1.7949, 0.3, 0);
		DiamondQ3.rotation.z = THREE.MathUtils.degToRad(-136);
		QParent.add(DiamondQ3);


		let Plat2 = platGLTF.scene; // Use the loaded GLTF from Promise.all

		let PmeshGeometry = null;
		Plat2.traverse((child) => {
			if (child.isMesh) {
				child.geometry.center();
				child.material.roughness = 0.05;
				child.material.metalness = 1;
				child.material.color.set(0xEAEAEA);
				child.material.envMap = environment2;
				child.material.envMapIntensity = 0.6;
				child.material.needsUpdate = true;
				if (!PmeshGeometry) PmeshGeometry = child.geometry.clone();
			}
		});
		Plat2.scale.set(0.25, 0.25, 0.25);
		Plat2.position.set(1.93815, 0.13965, 0);
		QParent.add(Plat2);
		let GoldQ2 = goldGLTF.scene;

		let GmeshGeometry = null;
		GoldQ2.traverse((child) => {
			if (child.isMesh) {
				child.geometry.center();
				child.material.roughness = 0.07;
				child.material.metalness = 1;
				child.material.color.set(0xFFC125);
				child.material.envMap = environment2;
				child.material.envMapIntensity = 1;
				child.material.needsUpdate = true;
				if (!GmeshGeometry) GmeshGeometry = child.geometry.clone();
			}
		});
		GoldQ2.scale.set(0.25, 0.25, 0.25);
		GoldQ2.position.set(-0.220665, 2.3568, 0);
		QParent.add(GoldQ2);
		QParent.scale.set(1.1, 1.1, 1.1);
		Pivot.add(QParent);
		Pivot.rotation.z = THREE.MathUtils.degToRad(136);

		// Animation state for intro sequence
		let introAnimationState = {
			phase: 'rotating', // 'rotating', 'tilting', 'complete'
			rotationProgress: 0,
			tiltProgress: 0,
			rotationSpeed: 0.003,
		};
		// Second Loading Stage, Declare variables in outer scope
		const loadingStage2Obj = new THREE.Group();
		//split screen variables starts here
		let sceneR;
		const slider = document.getElementById('lslider');
		const sliderWidth = slider.offsetWidth;
		const maxPos = window.innerWidth - (sliderWidth / 2); // Maximum position (window width minus half the slider width)
		let sliderPos = window.innerWidth;
		slider.setAttribute('aria-valuenow', sliderPos);
		slider.setAttribute('aria-valuemax', maxPos);
		slider.style.opacity = 0;
		let thickGrid;
		let smallGrid;
		let axisLines;
		//Gimbal and RingHelper variables starts here
		let gimbalRenderer = null;
		let gimbalScene;
		let gimbalCamera;
		let gimble = null;
		const viewHelperPanel = document.getElementById('viewHelper');
		let labelRenderer = null;
		const ringHelperPanel = document.getElementById('ringHelper');
		let ringRenderer = null;
		let ringScene;
		let ringCamera;
		let ringble = null;
		let DiamondMini = null;
		// Flags to track initialization state
		let Stage2Objloaded = false;
		let gimbalhelperloaded = false;
		let ringhelperloaded = false;

		function initializeStage2Obj() {
			sceneR = new THREE.Scene();
			sceneR.add(hemiLight.clone());
			sceneR.add(dirLight.clone());
			renderer.setScissorTest(true);
			// Create axis lines with LineSegments2 for better performance and consistency
			const createAxisLines = (lineWidth = 3) => {
				const axisGroup = new THREE.Group();
				// X-axis (red)
				const xGeometry = new LineSegmentsGeometry();
				xGeometry.setPositions([0, 0, 0, 20, 0, 0]);
				const xMaterial = new LineMaterial({
					color: 0xB41400,
					linewidth: lineWidth,
					alphaToCoverage: true
				});
				const xLine = new LineSegments2(xGeometry, xMaterial);
				xLine.computeLineDistances();
				axisGroup.add(xLine);
				// Z-axis (cyan) - note: yline and zline swaped to match rhino and matrix
				const zGeometry = new LineSegmentsGeometry();
				zGeometry.setPositions([0, 0, 0, 0, 0, -20]);
				const zMaterial = new LineMaterial({
					color: 0x009BA5,
					linewidth: lineWidth,
					alphaToCoverage: true
				});
				const zLine = new LineSegments2(zGeometry, zMaterial);
				zLine.computeLineDistances();
				axisGroup.add(zLine);
				// Method to update resolution for responsive lines
				axisGroup.updateResolution = (renderer) => {
					const size = renderer.getSize(new THREE.Vector2());
					xMaterial.resolution.set(size.width, size.height);
					zMaterial.resolution.set(size.width, size.height);
				};
				// Cleanup method
				axisGroup.dispose = () => {
					xGeometry.dispose();
					zGeometry.dispose();
					xMaterial.dispose();
					zMaterial.dispose();
				};
				return axisGroup;
			};
			axisLines = createAxisLines(3);
			loadingStage2Obj.add(axisLines);
			sceneR.add(axisLines.clone());
			const GRID_COLORS_LIGHT = [0x999999, 0x777777];
			const GRID_COLORS_DARK = [0x555555, 0x888888];
			const Ggrid = new THREE.Group();
			thickGrid = new ThickGridHelper(40, 8, GRID_COLORS_LIGHT, 2);
			smallGrid = new ThickGridHelper(40, 40, GRID_COLORS_DARK, 1);
			Ggrid.add(thickGrid);
			Ggrid.add(smallGrid);
			const Ggrid2 = new THREE.Group();
			const thickGrid2 = new ThickGridHelper(40, 8, GRID_COLORS_LIGHT, 2);
			const smallGrid2 = new ThickGridHelper(40, 40, GRID_COLORS_DARK, 1);
			Ggrid2.add(thickGrid2);
			Ggrid2.add(smallGrid2);
			loadingStage2Obj.add(Ggrid);
			sceneR.add(Ggrid2);
			const QParentM = new THREE.Object3D();
			// Create wireframe mesh version of diamond for QParentM
			const DiamondQ3mesh = new THREE.Mesh(
				makeDiamond.sharedGeometry,
				makeDiamond.wireframeMaterial
			);
			DiamondQ3mesh.scale.set(1, 1, 1);
			DiamondQ3mesh.position.set(1.7949, 0.3, 0);
			DiamondQ3mesh.rotation.z = THREE.MathUtils.degToRad(-136);
			QParentM.add(DiamondQ3mesh);
			// Create and add the wireframe mesh to sceneR
			const Plat2mesh = new THREE.Mesh(
				PmeshGeometry,
				new THREE.MeshStandardMaterial({
					wireframe: true,
					color: 0xC0C0C0
				})
			);
			Plat2mesh.scale.set(0.25, 0.25, 0.25);
			Plat2mesh.position.set(1.93815, 0.13965, 0);
			QParentM.add(Plat2mesh);
			// Create and add the wireframe mesh to sceneR
			const GoldQ2mesh = new THREE.Mesh(
				GmeshGeometry,
				new THREE.MeshStandardMaterial({
					wireframe: true,
					color: 0xFFC125
				})
			);
			GoldQ2mesh.scale.set(0.25, 0.25, 0.25);
			GoldQ2mesh.position.set(-0.220665, 2.3568, 0);
			QParentM.add(GoldQ2mesh);
			QParentM.scale.set(1.1, 1.1, 1.1);
			sceneR.add(QParentM);
			let TILLVSTQ;
			gltfLoader.loadAsync('models/TILLVISTQ.glb')
				.then((anotherGlb) => {
					TILLVSTQ = anotherGlb.scene;
					let meshGeometry = null;
					TILLVSTQ.traverse((child) => {
						if (child.isMesh) {
							child.geometry.center();
							child.material.color.set(0x669a00);
							if (!meshGeometry) meshGeometry = child.geometry.clone();
						}
					});
					TILLVSTQ.scale.set(50, 50, 50);
					TILLVSTQ.position.set(-0.18943, 2.6044, 0);
					loadingStage2Obj.add(TILLVSTQ);
					setGroupOpacity(loadingStage2Obj, 0);
					scene.add(loadingStage2Obj);
					// Create and add the wireframe mesh to sceneR
					if (meshGeometry) {
						const TILLVSTQmesh = new THREE.Mesh(
							meshGeometry,
							new THREE.MeshStandardMaterial({
								wireframe: true,
								color: 0x446600
							})
						);
						TILLVSTQmesh.scale.set(50, 50, 50);
						TILLVSTQmesh.position.set(-0.18943, 2.6044, 0);
						TILLVSTQmesh.rotation.x = Math.PI / 2; // Rotate 90 degrees around the X-axis
						sceneR.add(TILLVSTQmesh);
					}
				})
				.catch((error) => {
					console.error('Error loading TILLVSTQ.glb:', error);
				});
			Stage2Objloaded = true;
		}

		function initializeGimbal() {
			// Create gimbal scene and camera
			gimbalCamera = new THREE.PerspectiveCamera(75, 1, 0.1, 50);
			gimbalCamera.position.set(0, 1, 5);
			gimbalCamera.lookAt(0, 0, 0);
			// Create gimbal renderer
			try {
				gimbalRenderer = new THREE.WebGLRenderer({
					alpha: true
				});

				const gimbalGl = gimbalRenderer.getContext();
				if (!gimbalGl || gimbalGl.isContextLost()) {
					throw new Error('Gimbal WebGL context not available');
				}
				//CONTEXT LOSS HANDLING:
				const gimbalCanvas = gimbalRenderer.domElement;
				gimbalCanvas.addEventListener('webglcontextlost', function(event) {
					event.preventDefault();
					handleContextLoss('gimbal');
				});

				gimbalCanvas.addEventListener('webglcontextrestored', function() {
					handleContextRestored('gimbal');
				});
				gimbalRenderer.setSize(128, 128);
				gimbalRenderer.domElement.style.position = 'absolute';
				gimbalRenderer.domElement.style.zIndex = '10';
			} catch (error) {
				console.error('Failed to create gimbal renderer:', error);
				// Continue without gimbal renderer
			}
			viewHelperPanel.appendChild(gimbalRenderer.domElement);
			labelRenderer = new CSS2DRenderer();
			labelRenderer.setSize(128, 128);
			labelRenderer.domElement.style.pointerEvents = 'none';
			viewHelperPanel.appendChild(labelRenderer.domElement);

			gimble = new THREE.Object3D();
			gimbalScene = new THREE.Scene();
			gimbalScene.add(gimble);
			gimble.position.set(0, 0, 0);
			// Create shared materials for gimbal lines
			const gimbalMaterials = {
				yellow: new LineMaterial({
					color: 0xF5BE46,
					linewidth: 2,
					alphaToCoverage: true
				}),
				red: new LineMaterial({
					color: 0xB41400,
					linewidth: 2,
					alphaToCoverage: true
				}),
				cyan: new LineMaterial({
					color: 0x009BA5,
					linewidth: 2,
					alphaToCoverage: true
				})
			};
			// Update resolution function for gimbal materials
			const updateGimbalResolution = () => {
				const size = new THREE.Vector2(128, 128);
				Object.values(gimbalMaterials).forEach(material => {
					material.resolution.set(size.width, size.height);
				});
			};
			// Y-axis (yellow) - vertical line
			const zyGeometry = new LineSegmentsGeometry();
			zyGeometry.setPositions([0, 0, 0, 0, 2, 0]);
			const zylineL = new LineSegments2(zyGeometry, gimbalMaterials.yellow);
			zylineL.computeLineDistances();
			gimble.add(zylineL);

			const zDiv = document.createElement('div');
			zDiv.className = 'llabel';
			zDiv.style.color = 'rgb(245, 190, 70)';
			zDiv.textContent = "z";
			const zLabel = new CSS2DObject(zDiv);
			zLabel.position.set(0, 2, 0);
			zLabel.center.set(0, 1);
			gimble.add(zLabel);
			// X-axis (red)
			const xrGeometry = new LineSegmentsGeometry();
			xrGeometry.setPositions([0, 0, 0, 2, 0, 0]);
			const xrlineL = new LineSegments2(xrGeometry, gimbalMaterials.red);
			xrlineL.computeLineDistances();
			gimble.add(xrlineL);

			const xDiv = document.createElement('div');
			xDiv.className = 'llabel';
			xDiv.style.color = 'rgb(180, 20, 0)';
			xDiv.textContent = "x";
			const xLabel = new CSS2DObject(xDiv);
			xLabel.position.set(2, 0, 0);
			xLabel.center.set(0, 1);
			gimble.add(xLabel);
			// Z-axis (cyan) - depth line
			const ycGeometry = new LineSegmentsGeometry();
			ycGeometry.setPositions([0, 0, 0, 0, 0, -2]);
			const yclineL = new LineSegments2(ycGeometry, gimbalMaterials.cyan);
			yclineL.computeLineDistances();
			gimble.add(yclineL);

			const yDiv = document.createElement('div');
			yDiv.className = 'llabel';
			yDiv.style.color = 'rgb(0, 155, 165)';
			yDiv.textContent = "y";
			const yLabel = new CSS2DObject(yDiv);
			yLabel.position.set(0, 0, -2);
			yLabel.center.set(0, 1);
			gimble.add(yLabel);
			updateGimbalResolution();
			gimbalhelperloaded = true;
			fadein([viewHelperPanel]);
		}

		function initializeRingHelper() {
			ringScene = new THREE.Scene();
			ringCamera = new THREE.PerspectiveCamera(50, 1, 0.1, 50);
			ringCamera.position.set(0, 0, 5);
			ringCamera.lookAt(0, 0, 0);
			ringRenderer = new THREE.WebGLRenderer({
				alpha: true,
				antialias: false
			});
			ringRenderer.setSize(128, 128);
			ringRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Handle high DPI displays
			//CONTEXT LOSS HANDLING:
			const ringCanvas = ringRenderer.domElement;
			ringCanvas.addEventListener('webglcontextlost', function(event) {
				event.preventDefault();
				handleContextLoss('ring');
			});

			ringCanvas.addEventListener('webglcontextrestored', function() {
				handleContextRestored('ring');
			});
			ringRenderer.domElement.style.zIndex = '10';
			ringHelperPanel.appendChild(ringRenderer.domElement);
			ringble = new THREE.Object3D();
			ringScene.add(ringble);
			ringble.position.set(0, 0, 0);

			// Create shared material for ring curves with proper resolution
			const ringMaterial = new THREE.LineBasicMaterial({
				color: 0xF5BE46,
			});
			const curvePoints = [
				[
					[0, -6.29, 15.377],
					[0, -6.484, 18.415],
					[0, -5.289, 21.215],
					[0, -2.962, 23.178],
					[0, 0, 23.882],
					[0, 2.962, 23.178],
					[0, 5.289, 21.215],
					[0, 6.484, 18.415],
					[0, 6.29, 15.377]
				],
				[
					[0, -6.248, 19.362],
					[-13.111, -5.477, 13.852],
					[-18.748, -3.655, 0.838],
					[-13.327, -2.999, -12.229],
					[0, -2.773, -17.371],
					[13.327, -2.999, -12.229],
					[18.748, -3.655, 0.838],
					[13.111, -5.477, 13.852]
				],
				[
					[0, -3.415, -15.377],
					[10.92, -3.836, -10.873],
					[15.444, -4.853, 0],
					[10.92, -5.869, 10.873],
					[0, -6.29, 15.377],
					[-10.92, -5.869, 10.873],
					[-15.444, -4.853, 0],
					[-10.92, -3.836, -10.873]
				],
				[
					[0, 6.248, 19.362],
					[-13.111, 5.477, 13.852],
					[-18.748, 3.655, 0.838],
					[-13.327, 2.999, -12.229],
					[0, 2.773, -17.371],
					[13.327, 2.999, -12.229],
					[18.748, 3.655, 0.838],
					[13.111, 5.477, 13.852]
				],
				[
					[0, 3.415, -15.377],
					[10.92, 3.836, -10.873],
					[15.444, 4.853, 0],
					[10.92, 5.869, 10.873],
					[0, 6.29, 15.377],
					[-10.92, 5.869, 10.873],
					[-15.444, 4.853, 0],
					[-10.92, 3.836, -10.873]
				],
				[
					[-15.444, -4.853, 0],
					[-18.875, -3.431, 0],
					[-20.297, 0, 0],
					[-18.875, 3.431, 0],
					[-15.444, 4.853, 0]
				],
				[
					[0, -3.415, -15.377],
					[0, -2.415, -17.792],
					[0, 0, -18.792],
					[0, 2.415, -17.792],
					[0, 3.415, -15.377]
				],
				[
					[15.444, -4.853, 0],
					[18.875, -3.431, 0],
					[20.297, 0, 0],
					[18.875, 3.431, 0],
					[15.444, 4.853, 0]
				]
			];

			curvePoints.forEach((pointArray, index) => {
				const vectors = pointArray.map(p => new THREE.Vector3(p[0], p[1], p[2]));
				const curve = new THREE.CatmullRomCurve3(vectors, true);
				const loop = new THREE.BufferGeometry().setFromPoints(curve.getPoints(100));

				const ringmini = new THREE.Line(loop, ringMaterial);
				ringmini.rotation.x = -Math.PI / 2;
				ringmini.scale.set(0.07, 0.05, 0.07);
				ringmini.position.set(0, 0.2, 0);
				ringble.add(ringmini);
			});

			DiamondMini = makeDiamond(diamondGeo, {
				isStatic: false
			});
			DiamondMini.scale.set(0.5, 0.5, 0.5);
			DiamondMini.position.set(0, 1.78, 0);
			ringble.add(DiamondMini);
			ringhelperloaded = true;
			fadein([ringHelperPanel]);
		}
		//Gimbal and RingHelper code ends here
		setupContextLossHandlers();
		// Safari-specific optimizations
		const getBrowserInfo = () => {
			const userAgent = navigator.userAgent;
			const isChrome = /chrome/i.test(userAgent) && !/edg/i.test(userAgent);
			const isFirefox = /firefox/i.test(userAgent);
			const isEdge = /edg/i.test(userAgent);
			const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
			const isMobileSafari = /iPad|iPhone|iPod/.test(userAgent) && isSafari;
			const isDesktopSafari = isSafari && !isMobileSafari;

			return {
				isChrome,
				isFirefox,
				isEdge,
				isSafari,
				isMobileSafari,
				isDesktopSafari
			};
		};
		const optimizeForSafari = () => {
			const browser = getBrowserInfo();
			if (browser.isDesktopSafari) {
				console.log('Applying desktop Safari optimizations');
				if (!DiamondMini === null) {DiamondMini.material.uniforms.bounces.value = 2;}
				if (DiamondQ3.material.uniforms) {
					DiamondQ3.material.uniforms.bounces.value = 3;
				}
			}
		};
		optimizeForSafari();

		function setupContextLossHandlers() {
			if (renderer && renderer.domElement) {
				const canvas = renderer.domElement;

				canvas.addEventListener('webglcontextlost', function(event) {
					event.preventDefault();
					handleContextLoss('main');
				});

				canvas.addEventListener('webglcontextrestored', function() {
					handleContextRestored('main');
				});
			}
			// Gimbal renderer context loss
			if (gimbalRenderer && gimbalRenderer.domElement) {
				const gimbalCanvas = gimbalRenderer.domElement;

				gimbalCanvas.addEventListener('webglcontextlost', function(event) {
					event.preventDefault();
					handleContextLoss('gimbal');
				});

				gimbalCanvas.addEventListener('webglcontextrestored', function() {
					handleContextRestored('gimbal');
				});
			}
			// Ring renderer context loss
			if (ringRenderer && ringRenderer.domElement) {
				const ringCanvas = ringRenderer.domElement;

				ringCanvas.addEventListener('webglcontextlost', function(event) {
					event.preventDefault();
					handleContextLoss('ring');
				});

				ringCanvas.addEventListener('webglcontextrestored', function() {
					handleContextRestored('ring');
				});
			}
		}
		slider.style.touchAction = 'none'; // disable touch scroll
		slider.addEventListener('pointerdown', onPointerDown);
		window.addEventListener('resize', onWindowResize);
		controls.addEventListener('start', onStart);
		controls.addEventListener('end', onEnd);
		//Window beforeunload for cleanup (important for Safari)
		window.addEventListener('beforeunload', cleanupAll);
		let smoothReset = false;
		let timer = 0;
		let shouldAnimate = true;
		canvas.style.opacity = 0;
		viewHelperPanel.style.opacity = 0;
		ringHelperPanel.style.opacity = 0;

		function cleanupAll() {
			console.log('Starting comprehensive cleanup...');
			shouldAnimate = false;
			cleanupDiamonds();
			cleanupSceneResources(scene);
			if (sceneR) {
				cleanupSceneResources(sceneR);
			}
			if (gimbalScene) {
				cleanupSceneResources(gimbalScene);
			}
			if (ringScene) {
				cleanupSceneResources(ringScene);
			}
			if (thickGrid && thickGrid.dispose) thickGrid.dispose();
			if (smallGrid && smallGrid.dispose) smallGrid.dispose();
			if (axisLines && axisLines.dispose) axisLines.dispose();
			if (renderer) {
				renderer.dispose();
				renderer = null;
			}
			if (gimbalRenderer) {
				gimbalRenderer.dispose();
				gimbalRenderer = null;
			}
			if (ringRenderer) {
				ringRenderer.dispose();
				ringRenderer = null;
			}
			if (labelRenderer) {
				if (labelRenderer.domElement && labelRenderer.domElement.parentNode) {
					labelRenderer.domElement.parentNode.removeChild(labelRenderer.domElement);
				}
				labelRenderer = null;
			}
			if (scene) scene.clear();
			if (sceneR) sceneR.clear();
			if (gimbalScene) gimbalScene.clear();
			if (ringScene) ringScene.clear();
			console.log('Cleanup completed');
		}

		function cleanupSceneResources(sceneToClean) {
			if (!sceneToClean) return;
			sceneToClean.traverse((child) => {
				if (child.isMesh) {
					if (child.geometry) {
						// Dispose BVH if present
						if (child.geometry.boundsTree) {
							child.geometry.boundsTree = null;
						}
						child.geometry.dispose();
					}
					if (child.material) {
						if (Array.isArray(child.material)) {
							child.material.forEach(material => disposeMaterial(material));
						} else {
							disposeMaterial(child.material);
						}
					}
				}
				if (child.isLine || child.isLineSegments) {
					if (child.geometry) child.geometry.dispose();
					if (child.material) disposeMaterial(child.material);
				}
			});
		}

		function disposeMaterial(material) {
			if (!material) return;
			Object.values(material).forEach(value => {
				if (value && value.isTexture) {
					value.dispose();
				}
			});
			if (material.uniforms) {
				Object.values(material.uniforms).forEach(uniform => {
					if (uniform.value && uniform.value.isTexture) {
						uniform.value.dispose();
					}
					if (uniform.value && uniform.value.isCubeTexture) {
						uniform.value.dispose();
					}
				});
			}

			material.dispose();
		}

		function cleanupDiamonds() {
			if (makeDiamond.sharedGeometry) {
				// Clear BVH
				if (makeDiamond.sharedGeometry.boundsTree) {
					makeDiamond.sharedGeometry.boundsTree = null;
				}
				makeDiamond.sharedGeometry.dispose();
				makeDiamond.sharedGeometry = null;
			}

			if (makeDiamond.sharedCollider) {
				if (makeDiamond.sharedCollider.geometry) {
					makeDiamond.sharedCollider.geometry.dispose();
				}
				if (makeDiamond.sharedCollider.material) {
					disposeMaterial(makeDiamond.sharedCollider.material);
				}
				makeDiamond.sharedCollider = null;
			}

			if (makeDiamond.sharedBVH) {
				makeDiamond.sharedBVH = null;
			}

			if (makeDiamond.staticMaterial) {
				disposeMaterial(makeDiamond.staticMaterial);
				makeDiamond.staticMaterial = null;
			}

			if (makeDiamond.wireframeMaterial) {
				disposeMaterial(makeDiamond.wireframeMaterial);
				makeDiamond.wireframeMaterial = null;
			}
		}

		function reinitializeWebGL() {
			try {
				if (diamond && diamond.material && diamond.material.uniforms) {
					diamond.material.uniforms.resolution.value.set(window.innerWidth, window.innerHeight);
					diamond.material.needsUpdate = true;
				}
				updateGimbalResolution();
				if (axisLines && axisLines.updateResolution) {
					axisLines.updateResolution(renderer);
				}
				if (!shouldAnimate) {
					shouldAnimate = true;
					animate();
					fadein([canvas]);
				}
				console.log('WebGL context successfully restored');
			} catch (error) {
				console.error('Failed to reinitialize WebGL:', error);
			}
		}

		function onWindowResize() {
			const {
				width: clientWidth2,
				height: clientHeight2
			} = mainid.getBoundingClientRect();
			camera.aspect = clientWidth2 / clientHeight2;
			camera.updateProjectionMatrix();
			renderer.setSize(clientWidth2, clientHeight2);
			if (thickGrid && thickGrid.updateResolution) thickGrid.updateResolution(renderer);
			if (smallGrid && smallGrid.updateResolution) smallGrid.updateResolution(renderer);
			if (axisLines && axisLines.updateResolution) axisLines.updateResolution(renderer);
		}
		// starting new drag with OrbitCintrols -- recover the min.max values
		function onStart(event) {
			controls.minAzimuthAngle = -Infinity;
			controls.maxAzimuthAngle = Infinity;
			controls.minPolarAngle = 0;
			controls.maxPolarAngle = Math.PI;
			smoothReset = false;
		}
		// enging drag with OrbitControls -- activate smooth reset
		function onEnd(event) {
			smoothReset = true;
			timer = 0; // Reset the timer when starting a new drag
		}
		// function to smooth reset the OrbitControl camera's angles
		function doSmoothReset() {
			// Desired final tilt angles
			const targetAlpha = 0.00; // slight horizontal tilt (azimuthal)
			const targetBeta = -0.25; // slight vertical tilt (polar)
			const defaultDistance = 23; // Set your desired default zoom
			const lerpFactor = 0.999;
			const defaultTarget = new THREE.Vector3(0, -3, 0);
			const defaultCameraPosition = new THREE.Vector3(0.16049332246694262, 2.830024467318552, 22.519828131435272);
			// get current angles
			let alpha = controls.getAzimuthalAngle(),
				beta = controls.getPolarAngle() - Math.PI / 2;
			// Lerp angles towards target tilt
			alpha = lerpFactor * alpha + (1 - lerpFactor) * targetAlpha;
			beta = lerpFactor * beta + (1 - lerpFactor) * targetBeta;
			// if they are close to the reset values, just set these values
			if (Math.abs(alpha - targetAlpha) < 0.001) alpha = targetAlpha;
			if (Math.abs(beta - targetBeta) < 0.001) beta = targetBeta;
			// smooth change using manual lerp
			controls.minAzimuthAngle = alpha;
			controls.maxAzimuthAngle = controls.minAzimuthAngle;
			controls.minPolarAngle = Math.PI / 2 + beta;
			controls.maxPolarAngle = controls.minPolarAngle;
			// Restore target
			controls.target.lerp(defaultTarget, 1 - lerpFactor);
			// Restore camera position
			camera.position.lerp(defaultCameraPosition, 1 - lerpFactor);
			// Restore zoom
			// Lerp camera position towards default distance from controls.target
			const direction = new THREE.Vector3().subVectors(camera.position, controls.target).normalize();
			const currentDistance = camera.position.distanceTo(controls.target);
			const newDistance = lerpFactor * currentDistance + (1 - lerpFactor) * defaultDistance;
			camera.position.copy(controls.target).add(direction.multiplyScalar(newDistance));

			const targetSliderPos = window.innerWidth / 2;
			if (timer > 489) {
				if (slider.style.opacity == 0) {
					fadein([slider]);
				}
				sliderPos = lerpFactor * sliderPos + (1 - lerpFactor) * targetSliderPos;
				if (slider) {
					slider.style.left = sliderPos - (slider.offsetWidth / 2) + 'px';
					// Update ARIA attributes
					slider.setAttribute('aria-valuenow', sliderPos);
					// Update aria-valuetext for a more descriptive read-out
					const percentage = Math.round((sliderPos / window.innerWidth) * 100);
					slider.setAttribute('aria-valuetext', `Left panel ${percentage} percent, Right panel ${100 - percentage} percent`);
				}
			}
			// if the reset values are reached, exit smooth reset
			if (Math.abs(alpha - targetAlpha) < 0.001 &&
				Math.abs(beta - targetBeta) < 0.001 &&
				Math.abs(currentDistance - defaultDistance) < 0.01 &&
				camera.position.distanceTo(defaultCameraPosition) < 0.01 &&
				controls.target.distanceTo(defaultTarget) < 0.01 &&
				Math.abs(sliderPos - targetSliderPos) < 1
			) {
				camera.position.copy(defaultCameraPosition);
				controls.target.copy(defaultTarget);
				onStart();
				smoothReset = false;
				timer = 0;
			}
		}
		function updateIntroAnimation() {
			if (!QParent) return;
			if (introAnimationState.phase === 'rotating') {
				// Y-axis rotation (1 full revolutions = 2π radians)
				Pivot.rotation.y += introAnimationState.rotationSpeed;
				introAnimationState.rotationProgress += introAnimationState.rotationSpeed;
				if (introAnimationState.rotationProgress >= Math.PI * 2) {
					introAnimationState.phase = 'tilting';
				}
            } else if (introAnimationState.phase === 'tilting') {
                // Z-axis tilt back to 0 from 136 degrees
                const targetZ = 0;
                const currentZ = Pivot.rotation.z;
                const diff = targetZ - currentZ;

                if (Math.abs(diff) > 0.005) {
                    const startZ = THREE.MathUtils.degToRad(136);
                    const totalDistance = Math.abs(startZ - targetZ); // Total distance to travel
                    const distanceTraveled = Math.abs(startZ - currentZ);
                    const progress = distanceTraveled / totalDistance; // 0 to 1
                    const easeInOut = progress < 0.5 ?
                        7 * Math.pow(progress, 5) :
                        7 * Math.pow(0.5 - (progress - 0.5), 5);
                    const speed = introAnimationState.rotationSpeed * (0.6 + easeInOut * 1.2); // base speed + eased component
                    Pivot.rotation.z += Math.sign(diff) * speed;
                } else {
                    Pivot.rotation.z = targetZ;
                    introAnimationState.phase = 'complete';
                }
			} else if (introAnimationState.phase === 'complete') {
				isInitialAnimationComplete = true;
				initializeStage2Obj();
				fadeInGroup();
				initializeHelpersStaggered();
				optimizeForSafari();
			}
		}

		function setGroupOpacity(group, opacity) {
			group.traverse((child) => {
				if (child.isMesh) {
					child.material.transparent = true;
					child.material.opacity = opacity;
				}
			});
		}
		let currentOpacity = 0;
		const fadeInGroup = () => {
			doSmoothReset();
			if (currentOpacity < 1) {
				currentOpacity += 0.001; // Adjust increment for speed
				setGroupOpacity(loadingStage2Obj, currentOpacity);
				requestAnimationFrame(fadeInGroup);
			}
		};

		function fadein(elements) {
			const fadeInDuration = 1000; // Duration in milliseconds
			const startTime = Date.now();
			controls.enabled = true;

			function fade() {
				const elapsed = Date.now() - startTime;
				// Ease-out cubic for smoother finish
				const t = Math.min(elapsed / fadeInDuration, 1);
				const opacity = Math.pow(t, 3); // Ease-in cubic
				elements.forEach(element => {
					element.style.opacity = opacity;
				});
				if (t < 1) {
					requestAnimationFrame(fade);
				}
			}

			fade();
		}

		function fadeout() {
			const fadeOutDuration = 500; // Duration in milliseconds
			const startTime = Date.now();
			controls.enabled = false;


			function fade() {
				const elapsed = Date.now() - startTime;
				// Ease-out cubic for smoother finish
				const t = Math.min(elapsed / fadeOutDuration, 1);
				const opacity = 1 - Math.pow(1 - t, 3);
				canvas.style.opacity = 1 - opacity; // Fade out the canvas
				viewHelperPanel.style.opacity = 1 - opacity;
				ringHelperPanel.style.opacity = 1 - opacity;
				if (t < 1) {
					requestAnimationFrame(fade);
				} else {
					renderer.clear(); // Clear the renderer after fade out
					gimbalRenderer.clear();
					ringRenderer.clear();
				}
			}
			fade();
		}

		function onPointerDown() {
			if (event.isPrimary === false) return;
			controls.enabled = false;
			window.addEventListener('pointermove', onPointerMove);
			window.addEventListener('pointerup', onPointerUp);
			onStart();
		}

		function onPointerUp() {
			controls.enabled = true;
			window.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('pointerup', onPointerUp);
			onEnd();
		}

		function onPointerMove(e) {
			if (e.isPrimary === false) return; // Ensure the event is primary

			const minPos = sliderWidth / 2;

			// Calculate the new slider position, clamping it between minPos and maxPos
			sliderPos = Math.max(minPos, Math.min(maxPos, e.pageX));

			slider.style.left = sliderPos - (sliderWidth / 2) + 'px';

			// Update ARIA attributes
			slider.setAttribute('aria-valuenow', sliderPos);
			
			// Update aria-valuetext for a more descriptive read-out
			const percentage = Math.round((sliderPos / window.innerWidth) * 100);
			slider.setAttribute('aria-valuetext', `Left panel ${percentage} percent, Right panel ${100 - percentage} percent`);
		}

		function initializeHelpersStaggered() {
			// Initialize gimbal on next frame
			requestAnimationFrame(() => {
				initializeGimbal();

				// Initialize ring helper on the frame after that
				requestAnimationFrame(() => {
					initializeRingHelper();
				});
			});
		}

		function shutdownHelpers() {
			if (helpersShutdown) return;

			helpersShutdown = true;
			console.log('Shutting down helper renderers...');

			// Only fade out if helpers were actually initialized
			if (gimbalhelperloaded && viewHelperPanel) {
				viewHelperPanel.style.opacity = '0';
			}
			if (ringhelperloaded && ringHelperPanel) {
				ringHelperPanel.style.opacity = '0';
			}

			// Stop helper rendering flags
			gimbalhelperloaded = false;
			ringhelperloaded = false;

			// Only dispose if renderers were actually created
			if (gimbalRenderer) {
				try {
					gimbalRenderer.dispose();
					gimbalRenderer = null;
				} catch (e) {
					console.warn('Error disposing gimbal renderer:', e);
					gimbalRenderer = null;
				}
			}

			if (ringRenderer) {
				try {
					ringRenderer.dispose();
					ringRenderer = null;
				} catch (e) {
					console.warn('Error disposing ring renderer:', e);
					ringRenderer = null;
				}
			}

			if (labelRenderer && labelRenderer.domElement) {
				try {
					labelRenderer.domElement.remove();
					labelRenderer = null;
				} catch (e) {
					console.warn('Error removing label renderer:', e);
					labelRenderer = null;
				}
			}
		}

		function reinitializeGimbal() {
			// Don't try to reinitialize if main context is dead or helpers are shutdown
			if (mainContextLost || helpersShutdown) return;

			// Don't try to reinitialize if it was never initialized in the first place
			if (!gimbalhelperloaded) {
				console.log('Gimbal was never initialized, skipping reinitialization');
				return;
			}

			try {
				// Cleanup existing gimbal renderer if it exists
				if (gimbalRenderer) {
					gimbalRenderer.dispose();
					gimbalRenderer = null;
				}

				// Reset the flag before attempting reinitialization
				gimbalhelperloaded = false;

				// Reinitialize
				initializeGimbal();
				console.log('Gimbal helper reinitialized');
			} catch (error) {
				console.error('Failed to reinitialize gimbal:', error);
				gimbalhelperloaded = false;
			}
		}

		function reinitializeRingHelper() {
			// Don't try to reinitialize if main context is dead or helpers are shutdown
			if (mainContextLost || helpersShutdown) return;

			// Don't try to reinitialize if it was never initialized in the first place
			if (!ringhelperloaded) {
				console.log('Ring helper was never initialized, skipping reinitialization');
				return;
			}

			try {
				// Cleanup existing ring renderer if it exists
				if (ringRenderer) {
					ringRenderer.dispose();
					ringRenderer = null;
				}

				// Reset the flag before attempting reinitialization
				ringhelperloaded = false;

				// Reinitialize
				initializeRingHelper();
				console.log('Ring helper reinitialized');
			} catch (error) {
				console.error('Failed to reinitialize ring helper:', error);
				ringhelperloaded = false;
			}
		}
		// Animation Loop
		function animate() {
			if (!shouldAnimate) return; // Stop animation loop
			// CONTEXT CHECK:
			if (renderer.getContext() && renderer.getContext().isContextLost()) {
				console.log('Context lost during animation - stopping');
				shouldAnimate = false;
				fadeout();
				return;
			}
			if (!isInitialAnimationComplete) {
				updateIntroAnimation();
			}
			if (isInitialAnimationComplete) {
				if (timer < 500) timer++;
				if (smoothReset && timer > 499) doSmoothReset();
			}
			controls.update();
			renderer.clear();
			// Context checks before rendering:
			if (!renderer.getContext() || renderer.getContext().isContextLost()) {
				return; // Skip rendering if context is lost
			}
			renderer.setScissor(0, 0, sliderPos, window.innerHeight);
			renderer.render(scene, camera);
			if (isInitialAnimationComplete) {
				if (Stage2Objloaded) {
					renderer.setScissor(sliderPos, 0, window.innerWidth, window.innerHeight);
					renderer.render(sceneR, camera);
				}
				if (gimbalhelperloaded) {
					gimble.quaternion.copy(camera.quaternion).invert();
					if (!gimbalRenderer.getContext() || gimbalRenderer.getContext().isContextLost()) {
						console.log('Gimbal context lost during render');
					} else {
						gimbalRenderer.render(gimbalScene, gimbalCamera);
					}
					labelRenderer.render(gimbalScene, gimbalCamera);
				}
				if (ringhelperloaded) {
					ringble.quaternion.copy(camera.quaternion).invert();
					DiamondMini.rotation.y += 0.0025;
					if (!ringRenderer.getContext() || ringRenderer.getContext().isContextLost()) {
						console.log('Ring context lost during render');
					} else {
						ringRenderer.render(ringScene, ringCamera);
					}
				}
			}
			requestAnimationFrame(animate);

		}

		animate();
		fadein([canvas]);
		onEnd();
	}
	if (createRenderer()) {
		initializeScene();
	}
}
main();